import { ArrowLeftOutlined } from '@ant-design/icons'
import { List } from '@refinedev/antd'
import {
  IResourceComponentsProps
} from '@refinedev/core'
import { Space, Tabs } from 'antd'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { useDeleteUser } from '../../../utils/hooks/useDeleteUser.hook'
import { useNotificationOkCancel } from '../../../utils/hooks/useNotificationOkCancel.hook'
import { confirmModalDeleteUserProps, confirmModalUnsavedChangesProps } from '../../components/constants'
import '../../styles/index.css'
import { CustomerBanks } from './components/customer-bank'
import { CustomerBeneficiariesRemitters } from './components/customer-beneficiaries-remitters'
import { CustomerInformation } from './components/customer-infor'

export const CustomerEdit: FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const customerId = id ? parseInt(id, 10) : undefined;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tab = queryParams.get('tab');
  const [deletingId, setDeletingId] = useState<number>()
  const [activeTab, setActiveTab] = useState<string>((tab as string) || 'profile')
  const [pendingTab, setPendingTab] = useState<string | null>(null)
  const checkUnsavedChangesRef = useRef<(() => Promise<boolean>) | null>(null)
  const resetFormRef = useRef<(() => void) | null>(null)

  const { deleteUser } = useDeleteUser()

  const onOkDelete = useCallback(async () => {
    if (deletingId !== undefined) {
      setDeletingId(undefined)
      await deleteUser(deletingId)
      navigate('/customers')
    }
  }, [deletingId, deleteUser, navigate])

  const onCancelDelete = useCallback(() => {
    setDeletingId(undefined)
  }, [])

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteUserProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  })

  const onUnsavedChangesOk = useCallback(() => {
    if (resetFormRef.current) {
      resetFormRef.current()
    }
    if (pendingTab) {
      setActiveTab(pendingTab)
      setPendingTab(null)
    }
  }, [pendingTab])

  const onUnsavedChangesCancel = useCallback(() => {
    setPendingTab(null)
  }, [])

  const {
    openNotification: openUnsavedChangesNotification,
    contextHolder: unsavedChangesContextHolder,
  } = useNotificationOkCancel({
    ...confirmModalUnsavedChangesProps,
    onOk: onUnsavedChangesOk,
    onCancel: onUnsavedChangesCancel,
  })

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification()
    }
  }, [deletingId, openNotification])

  useEffect(() => {
    if (pendingTab !== null) {
      openUnsavedChangesNotification()
    }
  }, [pendingTab, openUnsavedChangesNotification])

  const refetchList = useCallback(() => {
    // This will be handled by individual components
  }, [])

  const handleTabChange = useCallback(
    (newTab: string) => {
      if (activeTab === 'profile' && checkUnsavedChangesRef.current) {
        checkUnsavedChangesRef.current().then((hasUnsavedChanges) => {
          if (hasUnsavedChanges) {
            setPendingTab(newTab)
          } else {
            setActiveTab(newTab)
          }
        })
      } else {
        setActiveTab(newTab)
      }
    },
    [activeTab]
  )

  const handleUnsavedChangesCheck = useCallback(
    (checkFn: () => Promise<boolean>, resetFn: () => void) => {
      checkUnsavedChangesRef.current = checkFn
      resetFormRef.current = resetFn
    },
    []
  )

  const handleBack = useCallback(() => {
    navigate('/customers')
  }, [navigate])

  const tabItems = [
    {
      key: 'profile',
      label: 'Profile',
      children: (
        <CustomerInformation
          editingId={customerId}
          refetchList={refetchList}
          onUnsavedChangesCheck={handleUnsavedChangesCheck}
          isActive={activeTab === 'profile'}
        />
      ),
    },
    {
      key: 'bank',
      label: "Banks",
      children: (
        <CustomerBanks customerId={customerId} isActive={activeTab === 'bank'} />
      ),
    },
    {
      key: 'beneficiaries-remitters',
      label: 'Beneficiaries & Remitters',
      children: (
        <CustomerBeneficiariesRemitters customerId={customerId} isActive={activeTab === 'beneficiaries-remitters'} />
      ),
    },
  ]

  if (!customerId) {
    return null;
  }

  return (
    <>
      <List 
        title={
          <Space>
            <ArrowLeftOutlined 
              onClick={handleBack}
              className="back-icon"
            />
            <span>Customer Details</span>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
        />
      </List>
      {contextHolder}
      {unsavedChangesContextHolder}
    </>
  )
}
